import { useMemo } from "react";
import { Box, GlobalStyles } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridReadyEvent,
	RowClassParams,
	RowClickedEvent,
} from "ag-grid-community";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import type { BilletWorkOrderRow } from "../models/BilletWorkOrders";

const CENTER_CELL = { textAlign: "center" } as const;
const RIGHT_CELL = { textAlign: "right" } as const;
const CENTER_HEADER = "ag-cell-center-header";

const formatInt = (v: unknown): string => {
	if (v === null || v === undefined || v === "") return "";
	const n = Number(v);
	if (!Number.isFinite(n)) return "";
	return n.toFixed(0);
};

const formatDecimal3 = (v: unknown): string => {
	if (v === null || v === undefined || v === "") return "";
	const n = Number(v);
	if (!Number.isFinite(n)) return "";
	return n.toFixed(3);
};

// The procedure returns raw `Start` as DATETIME; the client formats it.
const formatStart = (row: BilletWorkOrderRow | undefined): string =>
	row?.Start ? dxtToLocalServerTime(row.Start, "yyyy-MM-dd HH:mm:ss") : "";

const buildColumnDefs = (): ColDef<BilletWorkOrderRow>[] => [
	{
		field: "Start",
		headerName: "Start",
		width: 160,
		sortable: true,
		sort: "desc",
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatStart(p.data),
	},
	{
		field: "ProductName",
		headerName: "Product",
		flex: 1,
		minWidth: 160,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "WorkOrderName",
		headerName: "Work Order",
		flex: 1,
		minWidth: 140,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => p.value ?? "",
	},
	{
		field: "BilletSize",
		headerName: "Size",
		width: 90,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Length",
		headerName: "Length",
		width: 90,
		sortable: true,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatInt(p.value),
	},
	{
		field: "BilletWeight",
		headerName: "Billet Weight",
		width: 110,
		sortable: true,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatInt(p.value),
	},
	{
		field: "StartTemperature",
		headerName: "Start Temperature",
		width: 140,
		sortable: true,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatInt(p.value),
	},
	{
		field: "EndTemperature",
		headerName: "End Temperature",
		width: 140,
		sortable: true,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatDecimal3(p.value),
	},
	{
		field: "CrewId",
		headerName: "Shift",
		width: 70,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => p.value ?? "",
	},
];

interface Props {
	rows: BilletWorkOrderRow[];
	selectedRowId: number | null;
	onRowClicked: (row: BilletWorkOrderRow) => void;
	onGridReady: (event: GridReadyEvent<BilletWorkOrderRow>) => void;
}

export const BilletWorkOrdersTable = ({
	rows,
	selectedRowId,
	onRowClicked,
	onGridReady,
}: Props) => {
	const columnDefs = useMemo(() => buildColumnDefs(), []);

	const getRowStyle = (params: RowClassParams<BilletWorkOrderRow>) => {
		if (params.data?.Id === selectedRowId) {
			return { backgroundColor: "rgba(99, 102, 241, 0.12)" };
		}
		return undefined;
	};

	const handleRowClicked = (e: RowClickedEvent<BilletWorkOrderRow>) => {
		if (e.data) onRowClicked(e.data);
	};

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<AgGridReact<BilletWorkOrderRow>
				columnDefs={columnDefs}
				rowData={rows}
				rowModelType="clientSide"
				defaultColDef={{ resizable: true }}
				autoSizeStrategy={{ type: "fitGridWidth" }}
				suppressContextMenu
				headerHeight={32}
				rowHeight={28}
				onGridReady={onGridReady}
				onRowClicked={handleRowClicked}
				getRowStyle={getRowStyle}
				getRowId={(p) => String(p.data.Id)}
			/>
		</Box>
	);
};
